'use client';

import { lazy, Suspense } from 'react';
import { useIntl } from 'react-intl';
import { Card, CardHeader } from '@packages/shared/src/ui/Card/Card';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import type { JoePresentationState } from './JoeOverview.types';
import { JoeOverviewCardSkeleton } from './JoeOverviewCardSkeleton';
import { joeMessages } from './messages';

/**
 * Loaded on demand so the loading state does not ship the body, its Link/Button/Image
 * dependencies and the key visual just to render a skeleton.
 */
const JoeDisconnectedBody = lazy(() =>
  import('./JoeDisconnectedBody.client').then((mod) => ({
    default: mod.JoeDisconnectedBody,
  })),
);

/** Standalone jö states. `connected` is embedded in `CustomerCard` and never handled here. */
export type JoeStandaloneState = Exclude<JoePresentationState, { status: 'connected' }>;

export type JoeOverviewCardProps = {
  state: JoeStandaloneState;
};

/**
 * Standalone jö Bonus Club card for the My Account overview grid.
 *
 * Notification content is caller-owned - this component supplies the placement and severity
 * mapping, not production copy.
 */
export const JoeOverviewCard = ({ state }: JoeOverviewCardProps) => {
  const { formatMessage } = useIntl();

  if (state.status === 'loading') {
    return <JoeOverviewCardSkeleton />;
  }

  return (
    <Card className="w-full rounded-sm" data-testid="joe-overview-card">
      <CardHeader hasDivider>
        <h2 className="text-size-headline-base">{formatMessage(joeMessages.cardHeadline)}</h2>
      </CardHeader>

      {state.status === 'error' && (
        <div className="px-2 py-2" data-testid="joe-overview-error-notification">
          <Notification variant="outline" severity="error">
            {state.notification}
          </Notification>
        </div>
      )}

      {state.status === 'disconnected' && state.notification && (
        <div className="px-2 pt-2" data-testid="joe-overview-notification">
          <Notification variant="outline" severity={state.notification.severity}>
            {state.notification.content}
          </Notification>
        </div>
      )}

      {state.status === 'disconnected' && (
        <Suspense fallback={<JoeOverviewCardSkeleton />}>
          <JoeDisconnectedBody />
        </Suspense>
      )}
    </Card>
  );
};
